import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import switchJSON from '@/shared/animations/switch.json';
import { type ChainflipToken, type Token } from '@/shared/assets/tokens';
import useSwapRequestStore from '@/shared/hooks/useSwapRequestStore';
import { chainflipAssetMap } from '@/shared/utils/env';
import Lottie from '@/shared/utils/Lottie';
import { NUMBER_REGEX } from '@/shared/utils/regex';
import TokenAmount from '@/shared/utils/TokenAmount';
import TokenInputField from './TokenInputField';

const MAX_AMOUNT_PARAM_LENGTH = 40;

const queryParamToAmount = (str: string | string[] | undefined, token: Token | ChainflipToken) => {
  if (typeof str !== 'string' || str.length > MAX_AMOUNT_PARAM_LENGTH) return null;
  if (!NUMBER_REGEX.test(str)) return null;

  const amount: TokenAmount | null = token.chainflipId
    ? TokenAmount.fromAsset(str, token.chainflipId)
    : new TokenAmount(str, token.decimals);

  return amount?.gt(0) ? amount : null;
};

const queryParamToToken = (str: string | string[] | undefined) => {
  if (typeof str === 'string') {
    for (const token of Object.values(chainflipAssetMap)) {
      if (token.chainflipId.toLowerCase() === str.toLowerCase()) return token;
    }
  }
  return null;
};

export default function TokenFields({
  setValidator,
}: {
  setValidator: (name: 'srcAmount' | 'destAmount', cb: () => boolean) => void;
}) {
  const router = useRouter();
  const srcToken = useSwapRequestStore((state) => state.srcToken);
  const srcAmount = useSwapRequestStore((state) => state.srcAmount);
  const destToken = useSwapRequestStore((state) => state.destToken);
  const destAmount = useSwapRequestStore((state) => state.selectedRoute?.destAmount);
  const setSrcToken = useSwapRequestStore((state) => state.setSrcToken);
  const setSrcAmount = useSwapRequestStore((state) => state.setSrcAmount);
  const setDestToken = useSwapRequestStore((state) => state.setDestToken);

  const [switchState, setSwitchState] = useState<'fadeIn' | 'fadeOut' | null>(null);

  useEffect(() => {
    const { srcAsset, destAsset, srcAmount: srcAmountParam, ...remainingQuery } = router.query;
    const src = queryParamToToken(srcAsset);
    if (src) setSrcToken(src);
    const dest = queryParamToToken(destAsset);
    if (dest) setDestToken(dest);

    const amount = queryParamToAmount(srcAmountParam, src ?? srcToken);
    if (amount) setSrcAmount(amount);

    if (src || dest || amount) {
      router.replace({ pathname: router.pathname, query: remainingQuery });
    }
  }, [router.query.srcAsset, router.query.destAsset, router.query.srcAmount]);

  const onTokenSwitch = useCallback(() => {
    if (switchState === 'fadeIn') {
      if (destAmount?.gt(0)) {
        setSrcAmount(new TokenAmount(destAmount?.value ?? 0, destToken?.decimals));
      }
      setSrcToken(destToken);
      setDestToken(srcToken);
    }
  }, [srcToken, destToken, switchState, destAmount]);

  useEffect(onTokenSwitch, [switchState]);

  const onSwitchEnd = useCallback(() => {
    if (switchState === 'fadeOut') {
      setSwitchState('fadeIn');
    } else if (switchState === 'fadeIn') {
      setSwitchState(null);
    }
  }, [switchState]);

  return (
    <div className="flex w-full flex-col items-center gap-8 md:gap-4">
      <TokenInputField
        label="Deposit"
        token={srcToken}
        value={srcAmount}
        onChange={setSrcAmount}
        switchState={switchState}
        onSwitchEnd={onSwitchEnd}
        setInputAmountValidator={(cb) => setValidator('srcAmount', cb)}
      />
      <div
        className="rounded-md border border-cf-gray-4 bg-cf-gray-3-5 px-1.5 pt-1.5 transition duration-100 ease-out hover:border-cf-gray-5 hover:bg-cf-gray-4"
        onClick={(e) => {
          if (switchState !== null) {
            e.preventDefault();
          } else {
            setSwitchState('fadeOut');
          }
        }}
      >
        <Lottie
          as="button"
          animationData={switchJSON}
          autoplay={false}
          loop={false}
          className="h-[24px] w-[24px] rotate-90 stroke-white"
        />
      </div>
      <TokenInputField
        label="Receive"
        token={destToken}
        value={srcAmount && destAmount}
        readonly
        switchState={switchState}
        onSwitchEnd={onSwitchEnd}
        setInputAmountValidator={(cb) => setValidator('destAmount', cb)}
      />
    </div>
  );
}
